import api from './api'
import type {
  LearningTemplate,
  TemplateAssignment,
  CoachVisibleAttempt,
  TemplateListFilters,
  FindOrGeneratePayload,
  FindOrGenerateResult,
  TemplateQualitySummary,
  CreateTemplatePayload,
  CreateAssignmentPayload,
} from '../types/learningContent'

const compactParams = (params: object) => Object.fromEntries(
  Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
)

// Mentor/Admin + coach surfaces for the reusable learning content library. Learner-
// facing attempt submission uses the learning.* service.
export const learningContentService = {
  listTemplates: (filters: TemplateListFilters = {}) =>
    api.get<{ success: boolean; templates: LearningTemplate[] }>('/api/learning-content/templates', { params: compactParams(filters) }),
  getTemplate: (id: string) =>
    api.get<{ success: boolean; template: LearningTemplate; attempts: CoachVisibleAttempt[] }>(`/api/learning-content/templates/${id}`),
  listReviewQueue: () =>
    api.get<{ success: boolean; templates: LearningTemplate[] }>('/api/learning-content/review-queue'),
  getQualitySummary: (id: string) =>
    api.get<{ success: boolean; quality: TemplateQualitySummary }>(`/api/learning-content/templates/${id}/quality-summary`),
  createTemplate: (payload: CreateTemplatePayload) =>
    api.post<{ success: boolean; template: LearningTemplate }>('/api/learning-content/templates', payload),
  updateTemplate: (id: string, payload: Partial<Pick<LearningTemplate, 'status' | 'coachNotes' | 'qualityScore' | 'competency' | 'concepts' | 'competencyMapping' | 'certificationMapping'>>) =>
    api.patch<{ success: boolean; template: LearningTemplate }>(`/api/learning-content/templates/${id}`, payload),
  markReviewed: (id: string) =>
    api.post<{ success: boolean; template: LearningTemplate }>(`/api/learning-content/templates/${id}/reviewed`, {}),
  approveTemplate: (id: string) =>
    api.post<{ success: boolean; template: LearningTemplate }>(`/api/learning-content/templates/${id}/approve`, {}),
  archiveTemplate: (id: string) =>
    api.post<{ success: boolean; template: LearningTemplate }>(`/api/learning-content/templates/${id}/archive`, {}),
  customizeTemplate: (id: string, payload: Record<string, unknown>) =>
    api.post<{ success: boolean; template: LearningTemplate; parentTemplateId: string }>(`/api/learning-content/templates/${id}/customize`, payload),
  findOrGenerate: (payload: FindOrGeneratePayload) =>
    api.post<FindOrGenerateResult>('/api/learning-content/find-or-generate', payload),
  listAssignments: () =>
    api.get<{ success: boolean; assignments: TemplateAssignment[] }>('/api/learning-content/assignments'),
  createAssignment: (payload: CreateAssignmentPayload) =>
    api.post<{ success: boolean; assignment: TemplateAssignment }>('/api/learning-content/assignments', payload),
  listLearnerAttempts: (learnerId: string) =>
    api.get<{ success: boolean; attempts: CoachVisibleAttempt[] }>(`/api/learning-content/learners/${learnerId}/attempts`),
  addAttemptFeedback: (attemptId: string, feedback: string) =>
    api.post<{ success: boolean; attempt: CoachVisibleAttempt | null }>(`/api/learning-content/attempts/${attemptId}/feedback`, { feedback }),
}
